import React, { useState, useEffect } from 'react';
import {
  Video,
  Calendar,
  Clock,
  Star,
  Shield,
  Globe,
  Users,
  Heart,
  Play,
  CheckCircle,
  ArrowRight,
  Phone,
  MessageSquare,
  FileText,
  Award,
  Search,
  Filter,
  MapPin,
  Languages,
  Stethoscope,
  Camera,
  Mic,
  MicOff,
  VideoOff,
  PhoneCall,
  X,
  User
} from 'lucide-react';

const VideoConsultations = () => {
  const [activeTab, setActiveTab] = useState('book');
  const [selectedVet, setSelectedVet] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');

  const specialties = [
    { id: 'all', name: 'All Specialties' },
    { id: 'general', name: 'General Practice' },
    { id: 'surgery', name: 'Surgery' },
    { id: 'dermatology', name: 'Dermatology' },
    { id: 'cardiology', name: 'Cardiology' },
    { id: 'emergency', name: 'Emergency Care' },
    { id: 'livestock', name: 'Livestock' },
    { id: 'exotic', name: 'Exotic Animals' }
  ];

  const languages = [
    { id: 'all', name: 'All Languages' },
    { id: 'english', name: 'English' },
    { id: 'hindi', name: 'Hindi' },
    { id: 'marathi', name: 'Marathi' },
    { id: 'tamil', name: 'Tamil' },
    { id: 'bengali', name: 'Bengali' },
    { id: 'gujarati', name: 'Gujarati' }
  ];

  const veterinarians = [
    {
      id: 1,
      name: 'Dr. Priya Sharma',
      specialty: 'General Practice',
      experience: '8 years',
      rating: 4.9,
      reviews: 245,
      languages: ['English', 'Hindi', 'Marathi'],
      location: 'Mumbai, Maharashtra',
      price: 299,
      availability: 'Available Now',
      image: '/api/placeholder/100/100',
      about: 'Specialized in companion animal care with extensive experience in preventive medicine and emergency care.',
      nextSlot: '2:30 PM'
    },
    {
      id: 2,
      name: 'Dr. Rajesh Kumar',
      specialty: 'Surgery',
      experience: '12 years',
      rating: 4.8,
      reviews: 189,
      languages: ['English', 'Hindi', 'Punjabi'],
      location: 'Delhi, NCR',
      price: 399,
      availability: 'Available in 15 mins',
      image: '/api/placeholder/100/100',
      about: 'Expert in soft tissue and orthopedic surgery with focus on minimally invasive procedures.',
      nextSlot: '3:00 PM'
    },
    {
      id: 3,
      name: 'Dr. Meera Patel',
      specialty: 'Livestock',
      experience: '15 years',
      rating: 4.9,
      reviews: 312,
      languages: ['English', 'Hindi', 'Gujarati'],
      location: 'Ahmedabad, Gujarat',
      price: 249,
      availability: 'Available Now',
      image: '/api/placeholder/100/100',
      about: 'Specialized in large animal medicine and reproductive health with focus on dairy and poultry.',
      nextSlot: '2:45 PM'
    },
    {
      id: 4,
      name: 'Dr. Arjun Reddy',
      specialty: 'Emergency Care',
      experience: '10 years',
      rating: 4.7,
      reviews: 156,
      languages: ['English', 'Hindi', 'Telugu'],
      location: 'Hyderabad, Telangana',
      price: 349,
      availability: 'Available Now',
      image: '/api/placeholder/100/100',
      about: 'Emergency and critical care specialist with expertise in trauma and intensive care.',
      nextSlot: '2:15 PM'
    }
  ];

  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM'
  ];

  const upcomingConsultations = [
    {
      id: 1,
      vetName: 'Dr. Priya Sharma',
      date: 'Today',
      time: '3:30 PM',
      petName: 'Buddy',
      type: 'General Checkup',
      status: 'confirmed'
    },
    {
      id: 2,
      vetName: 'Dr. Rajesh Kumar',
      date: 'Tomorrow',
      time: '10:00 AM',
      petName: 'Mittens',
      type: 'Surgery Follow-up',
      status: 'pending'
    }
  ];

  const filteredVets = veterinarians.filter(vet => {
    const matchesSearch = vet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vet.specialty.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpecialty = selectedSpecialty === 'all' || vet.specialty.toLowerCase().includes(selectedSpecialty);
    const matchesLanguage = selectedLanguage === 'all' || 
                           vet.languages.some(lang => lang.toLowerCase().includes(selectedLanguage));
    return matchesSearch && matchesSpecialty && matchesLanguage;
  });

  const handleBookConsultation = (vet) => {
    setSelectedVet(vet);
    setShowBookingModal(true);
  };

  const confirmBooking = () => {
    if (selectedVet && selectedDate && selectedTime) {
      alert(`Consultation booked with ${selectedVet.name} on ${selectedDate} at ${selectedTime}`);
      setShowBookingModal(false);
      setSelectedVet(null);
      setSelectedDate('');
      setSelectedTime('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-green-600 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Video Consultations</h1>
                <p className="text-sm text-gray-500">Connect with expert veterinarians instantly</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-green-100 px-3 py-1 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-green-700 text-sm font-medium">24/7 Available</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-white p-1 rounded-lg shadow-sm">
            <button
              onClick={() => setActiveTab('book')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'book'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Book Consultation
            </button>
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'upcoming'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Upcoming Consultations
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'history'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Consultation History
            </button>
          </div>
        </div>

        {/* Book Consultation Tab */}
        {activeTab === 'book' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search veterinarians..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={selectedSpecialty}
                  onChange={(e) => setSelectedSpecialty(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {specialties.map(specialty => (
                    <option key={specialty.id} value={specialty.id}>
                      {specialty.name}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {languages.map(language => (
                    <option key={language.id} value={language.id}>
                      {language.name}
                    </option>
                  ))}
                </select>
                <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2">
                  <Filter className="w-4 h-4" />
                  <span>Filter</span>
                </button>
              </div>
            </div>

            {/* Veterinarian List */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredVets.map(vet => (
                <div key={vet.id} className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-green-600 rounded-full flex items-center justify-center">
                        <User className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{vet.name}</h3>
                          <div className="flex items-center space-x-1">
                            <div className={`w-2 h-2 rounded-full ${vet.availability === 'Available Now' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                            <span className={`text-xs font-medium ${vet.availability === 'Available Now' ? 'text-green-700' : 'text-yellow-700'}`}>
                              {vet.availability}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                          <div className="flex items-center space-x-1">
                            <Stethoscope className="w-4 h-4" />
                            <span>{vet.specialty}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Award className="w-4 h-4" />
                            <span>{vet.experience}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center space-x-1">
                            <Star className="w-4 h-4 text-yellow-400 fill-current" />
                            <span>{vet.rating} ({vet.reviews} reviews)</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MapPin className="w-4 h-4" />
                            <span>{vet.location}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 mb-4">
                          <Languages className="w-4 h-4 text-gray-400" />
                          <div className="flex flex-wrap gap-1">
                            {vet.languages.map(lang => (
                              <span key={lang} className="text-xs bg-gray-100 px-2 py-1 rounded">
                                {lang}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="text-2xl font-bold text-blue-600">₹{vet.price}</div>
                          <div className="text-sm text-gray-500">Next slot: {vet.nextSlot}</div>
                        </div>
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleBookConsultation(vet)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                          >
                            <Video className="w-4 h-4" />
                            <span>Book Now</span>
                          </button>
                          <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                            View Profile
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Consultations Tab */}
        {activeTab === 'upcoming' && (
          <div className="space-y-6">
            {upcomingConsultations.map(consultation => (
              <div key={consultation.id} className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-green-600 rounded-full flex items-center justify-center">
                      <Video className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{consultation.vetName}</h3>
                      <p className="text-sm text-gray-600">{consultation.type} for {consultation.petName}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{consultation.date}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{consultation.time}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      consultation.status === 'confirmed' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {consultation.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                    </div>
                    <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                      <Play className="w-4 h-4" />
                      <span>Join Call</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Consultation History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No consultation history yet</h3>
              <p className="text-gray-600">Your past consultations will appear here</p>
            </div>
          </div>
        )}
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedVet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Book Consultation</h3>
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Vet Info */}
              <div className="flex items-center space-x-3 mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-green-600 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{selectedVet.name}</h4>
                  <p className="text-sm text-gray-600">{selectedVet.specialty}</p>
                  <p className="text-sm font-medium text-blue-600">₹{selectedVet.price}</p>
                </div>
              </div>

              {/* Date Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Time Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Time</label>
                <div className="grid grid-cols-3 gap-2">
                  {timeSlots.map(time => (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`py-2 px-3 text-sm rounded-lg border transition-colors ${
                        selectedTime === time
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>

              {/* Booking Summary */}
              <div className="border-t pt-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">Consultation Fee</span>
                  <span className="font-semibold">₹{selectedVet.price}</span>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">Platform Fee</span>
                  <span className="font-semibold">₹49</span>
                </div>
                <div className="flex justify-between items-center text-lg font-bold border-t pt-4">
                  <span>Total</span>
                  <span>₹{selectedVet.price + 49}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmBooking}
                  disabled={!selectedDate || !selectedTime}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Confirm Booking
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoConsultations;